import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLang } from "../i18n/LangContext";
import { useAuth } from "../contexts/AuthContext";
import { api, extractError } from "../api/client";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { AUTH } from "../constants/testIds";
import { ERROR_CODE_MAP } from "../i18n/translations";
import { roleHome } from "../lib/roleHome";
import { ShieldCheck, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { t } = useLang();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState("");
  const [unverified, setUnverified] = useState(false);
  const [resent, setResent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setUnverified(false);
    setResent(false);
    setBusy(true);
    try {
      const user = await login(email, password, rememberMe);
      navigate(roleHome(user), { replace: true });
    } catch (err) {
      const { code, message } = extractError(err);
      if (code === "AUTH_EMAIL_NOT_VERIFIED") setUnverified(true);
      const key = ERROR_CODE_MAP[code];
      setError(key ? t(`auth.${key}`) : message);
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    try {
      await api.post("/auth/resend-verification", { email });
    } catch (err) {
      console.error("resend-verification failed", err);
    }
    setResent(true);
  };

  return (
    <div className="space-y-8">
      <div className="space-y-2">
        <h1 className="font-[Outfit] text-3xl sm:text-4xl font-extrabold text-white tracking-tight">{t("auth.loginTitle")}</h1>
        <p className="text-slate-400 text-sm">{t("auth.loginSubtitle")}</p>
      </div>
      <form onSubmit={submit} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-slate-300">{t("common.email")}</Label>
          <Input
            data-testid={AUTH.loginEmail}
            id="email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bg-[#1C202E] border-white/10 text-white h-11 focus-visible:ring-[#FF3B30]/40"
            placeholder="you@example.com"
          />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password" className="text-slate-300">{t("common.password")}</Label>
            <Link data-testid={AUTH.toForgot} to="/forgot-password" className="text-xs text-[#FF3B30] hover:text-[#ff6b62] font-medium">
              {t("auth.forgotLink")}
            </Link>
          </div>
          <div className="relative">
            <Input
              data-testid={AUTH.loginPassword}
              id="password"
              type={showPassword ? "text" : "password"}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-[#1C202E] border-white/10 text-white h-11 pr-11 focus-visible:ring-[#FF3B30]/40"
              placeholder="••••••••"
            />
            <button
              type="button"
              data-testid="login-password-toggle"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2.5">
          <Checkbox
            id="remember-me"
            data-testid="login-remember-me"
            checked={rememberMe}
            onCheckedChange={(v) => setRememberMe(v === true)}
            className="border-white/20 data-[state=checked]:bg-[#FF3B30] data-[state=checked]:border-[#FF3B30]"
          />
          <Label htmlFor="remember-me" className="text-sm text-slate-300 cursor-pointer select-none">
            {t("auth.rememberMe")}
          </Label>
        </div>
        {error && (
          <div data-testid={AUTH.loginError} className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {error}
          </div>
        )}
        {unverified && (
          <div className="space-y-2">
            <Button
              data-testid={AUTH.resendVerification}
              type="button"
              variant="outline"
              onClick={resend}
              className="w-full h-10 border-white/15 bg-transparent text-slate-200 hover:bg-white/5 hover:text-white"
            >
              {t("auth.resendVerification")}
            </Button>
            {resent && <p className="text-emerald-300 text-xs">{t("auth.resendSent")}</p>}
          </div>
        )}
        <Button
          data-testid={AUTH.loginSubmit}
          type="submit"
          disabled={busy}
          className="w-full h-11 bg-[#FF3B30] hover:bg-[#E02E24] text-white font-semibold shadow-lg shadow-red-900/20"
        >
          {busy ? t("common.loading") : t("auth.loginSubmit")}
        </Button>
      </form>

      <div className="text-center text-sm text-slate-400">
        {t("auth.noAccount")}{" "}
        <Link data-testid={AUTH.toRegister} to="/register" className="text-white font-semibold hover:text-[#FF3B30]">
          {t("auth.registerLink")}
        </Link>
      </div>

      <div data-testid="login-coach-info" className="rounded-xl border border-white/5 bg-white/[0.02] p-4 flex gap-3">
        <ShieldCheck className="h-5 w-5 text-[#10B981] shrink-0 mt-0.5" />
        <div className="space-y-1">
          <div className="text-sm font-semibold text-white">{t("auth.coachInfoTitle")}</div>
          <p className="text-xs leading-relaxed text-slate-400">{t("auth.coachInfoBody")}</p>
        </div>
      </div>
    </div>
  );
}
